// =============================================================================
// features/admin/inbox-assignments/InboxAssignmentsPage.tsx — Tela de
// atribuição usuário↔canal (F47-S08).
//
// Quem responde por qual inbox do live chat. Existe para o que hoje não tem
// como ser dito: "este gestor responde pela API oficial" — a caixa WAHA
// própria do vendedor (número pessoal pareado) já é resolvida sozinha
// (`agents.user_id`) e NÃO aparece aqui.
//
// O CRUD (backend) NÃO é gateado por `livechat.inbox_scope.enabled` de
// propósito — a tela precisa funcionar ANTES de ligar a flag, para o admin
// pré-popular as atribuições (ordem de aplicação do slot). Enquanto a flag
// estiver desligada, atribuir aqui não muda NADA na visibilidade do live
// chat ainda — a tela avisa isso explicitamente para não deixar o operador
// achar que já surtiu efeito.
//
// RBAC: livechat:inbox_assignment:manage (admin/gestor_geral — backend valida).
// LGPD: nenhum PII de titular aqui — só nome/e-mail de FUNCIONÁRIO
// (Art. 7º, IX) e metadados de canal.
// =============================================================================
import * as React from 'react';

import { Button } from '../../../components/ui/Button';
import { useToast } from '../../../components/ui/Toast';
import { useUsers } from '../../../hooks/admin/useUsers';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { cn } from '../../../lib/cn';
import { useChannels } from '../../configuracoes/canais/useChannels';

import type { ChannelAssignmentResponse } from './api';
import { useCreateInboxAssignment, useDeleteInboxAssignment, useInboxAssignments } from './hooks';

const PROVIDER_LABEL: Record<string, string> = {
  meta_whatsapp: 'WhatsApp (API oficial)',
  meta_instagram: 'Instagram',
  waha: 'WhatsApp pessoal (WAHA)',
};

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function TableSkeleton(): React.JSX.Element {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <tr key={i} aria-hidden="true">
          {Array.from({ length: 4 }).map((__, j) => (
            <td key={j} className="px-4 py-3.5">
              <div
                className="h-4 rounded-xs animate-pulse"
                style={{ width: 60 + ((i * 19 + j * 11) % 90), background: 'var(--surface-muted)' }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Formulário de nova atribuição
// ---------------------------------------------------------------------------

function CreateAssignmentForm(): React.JSX.Element {
  const { data: usersData } = useUsers({ limit: 200, active: 'true' });
  const { channels } = useChannels();
  const { mutate: create, isPending } = useCreateInboxAssignment();
  const { toast } = useToast();

  const [userId, setUserId] = React.useState('');
  const [channelId, setChannelId] = React.useState('');

  const users = usersData?.data ?? [];

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (userId === '' || channelId === '') {
      toast('Selecione um usuário e um canal.', 'danger');
      return;
    }
    create({ userId, channelId });
  };

  const selectClasses = cn(
    'h-10 rounded-sm border border-border-strong px-3 font-sans text-sm text-ink bg-surface-1',
    'shadow-[inset_0_1px_3px_rgba(0,0,0,0.06)]',
    'focus:outline-none focus:border-[var(--brand-azul)]',
    'focus:ring-3 focus:ring-[rgba(27,58,140,0.15)]',
    'transition-[border-color,box-shadow] duration-[150ms]',
  );

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col sm:flex-row sm:items-end gap-3 rounded-lg border border-border p-4"
      style={{ background: 'var(--bg-elev-1)' }}
    >
      <div className="flex flex-col gap-1 flex-1 min-w-0">
        <label htmlFor="inbox-assignment-user" className="font-sans text-xs text-ink-3">
          Usuário
        </label>
        <select
          id="inbox-assignment-user"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          className={selectClasses}
        >
          <option value="">Selecione um usuário…</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.fullName} ({u.email})
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 min-w-0">
        <label htmlFor="inbox-assignment-channel" className="font-sans text-xs text-ink-3">
          Canal (inbox)
        </label>
        <select
          id="inbox-assignment-channel"
          value={channelId}
          onChange={(e) => setChannelId(e.target.value)}
          className={selectClasses}
        >
          <option value="">Selecione um canal…</option>
          {channels.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} — {PROVIDER_LABEL[c.provider] ?? c.provider}
            </option>
          ))}
        </select>
      </div>

      <Button type="submit" disabled={isPending}>
        {isPending ? 'Atribuindo…' : 'Atribuir'}
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Linha da tabela
// ---------------------------------------------------------------------------

function AssignmentRow({
  assignment,
}: {
  assignment: ChannelAssignmentResponse;
}): React.JSX.Element {
  const { mutate: remove, isPending } = useDeleteInboxAssignment();

  return (
    <tr className="border-b border-border transition-colors duration-[150ms] hover:bg-[var(--surface-muted)]">
      <td className="px-4 py-3.5 font-sans text-sm text-ink">
        <div>{assignment.userFullName}</div>
        <div className="text-xs text-ink-4">{assignment.userEmail}</div>
      </td>
      <td className="px-4 py-3.5 font-sans text-sm text-ink">{assignment.channelName}</td>
      <td className="px-4 py-3.5 font-sans text-sm text-ink-3">
        {PROVIDER_LABEL[assignment.channelProvider] ?? assignment.channelProvider}
      </td>
      <td className="px-4 py-3.5 text-right">
        <Button
          variant="ghost"
          size="sm"
          disabled={isPending}
          onClick={() => remove(assignment.id)}
          aria-label={`Remover atribuição de ${assignment.userFullName} — ${assignment.channelName}`}
        >
          Remover
        </Button>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export function InboxAssignmentsPage(): React.JSX.Element {
  const { enabled: flagEnabled, isLoading: flagLoading } = useFeatureFlag(
    'livechat.inbox_scope.enabled',
  );
  const { data: assignments, isLoading, isError, refetch } = useInboxAssignments();

  return (
    <div className="flex flex-col gap-6 pb-12">
      <div>
        <h1
          className="font-display font-bold text-ink"
          style={{
            fontSize: 'var(--text-3xl)',
            letterSpacing: '-0.04em',
            lineHeight: '1',
            fontVariationSettings: "'opsz' 32",
          }}
        >
          Atribuição de Inboxes
        </h1>
        <p className="mt-1.5 font-sans text-ink-3" style={{ fontSize: 'var(--text-sm)' }}>
          Diga quem responde por qual caixa do live chat. A caixa WhatsApp pessoal do vendedor já é
          automática — atribua aqui só as inboxes que ele não é dono, como a API oficial.
        </p>
      </div>

      {!flagLoading && !flagEnabled ? (
        <div
          className="rounded-lg border px-4 py-3 font-sans text-sm"
          style={{
            background: 'var(--warning-bg, rgba(217,119,6,0.08))',
            borderColor: 'var(--warning-border, rgba(217,119,6,0.3))',
            color: 'var(--ink-2)',
          }}
        >
          A regra de escopo por inbox ainda está desligada (`livechat.inbox_scope.enabled`).
          Atribuições criadas aqui ficam prontas, mas não mudam quem enxerga qual conversa até a
          flag ser ligada.
        </div>
      ) : null}

      <CreateAssignmentForm />

      <div
        className="rounded-lg border border-border overflow-hidden"
        style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-1)' }}
      >
        <table className="w-full border-collapse text-sm">
          <thead style={{ background: 'var(--surface-muted)' }}>
            <tr>
              <th className="px-4 py-2.5 text-left font-sans text-xs font-medium text-ink-3">
                Usuário
              </th>
              <th className="px-4 py-2.5 text-left font-sans text-xs font-medium text-ink-3">
                Canal
              </th>
              <th className="px-4 py-2.5 text-left font-sans text-xs font-medium text-ink-3">
                Provedor
              </th>
              <th className="px-4 py-2.5 text-right font-sans text-xs font-medium text-ink-3">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? <TableSkeleton /> : null}
            {isError ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center font-sans text-sm text-ink-3">
                  Falha ao carregar atribuições.{' '}
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="text-[var(--brand-azul)] underline"
                  >
                    Tentar novamente
                  </button>
                </td>
              </tr>
            ) : null}
            {!isLoading && !isError && (assignments?.length ?? 0) === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center font-sans text-sm text-ink-3">
                  Nenhuma atribuição ainda.
                </td>
              </tr>
            ) : null}
            {!isLoading &&
              !isError &&
              (assignments ?? []).map((a) => <AssignmentRow key={a.id} assignment={a} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
